import React, { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { Field, useField } from "react-final-form";
import Feedback from "ui/Form/Element/Feedback";

export type AutocompleteSuggestion = {
    label: string;
    value: string | number;
};

export interface InputProps
    extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    name: string;
    dataFetcher(params?: HashMap<any>): Promise<AxiosResponse<any>>;
    defaultFetcherParams?: any;
    dataValue: string;
    dataLabel: string | ((data: any) => string);
    keySearch: string;
    values: OptionsInterface[];
    valueKeySearch?: string;
    defaultParams?: HashMap<any>;
    onSelectOptionCallback?: (value: any, record: any, selectedValues: OptionsInterface[]) => void;
    onPreSelectCallBack?: (value: any, record: any, selectedValues: OptionsInterface[]) => void;
    titleProps?: {
        enabled: boolean;
    };
    onClearCallBack?: () => void;
    minSearch?: number;
}

export type AutocompleteSelectProps = {
    error?: boolean;
    errorMessage?: string;
    inputProps: InputProps;
};

export type OptionsInterface = {
    value: string;
    label: string;
};

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({ error, errorMessage, inputProps }) => {
    const [optionsDisplay, setOptionsDisplay] = useState<boolean>(false);
    const [options, setOptions] = useState<Array<OptionsInterface>>([]);
    const [records, setRecords] = useState<Array<any>>([]);
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
    const { meta, input } = useField(inputProps.name);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const autocompleteFieldName = `autocomplete_${inputProps.name}`;

    const fieldError = !!(meta.error || meta.submitError);
    const fieldErrorMessage = meta.error || meta.submitError;

    const minSearch = inputProps?.minSearch ?? 2;

    const handleEvent = (e: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) => {
        const value: string = e.currentTarget.value;

        if (value.length < minSearch) {
            setOptionsDisplay(false);
            return;
        }

        let values: Array<OptionsInterface> = [];
        let params: HashMap<any> = Object.assign({}, inputProps.defaultParams, {
            [inputProps.keySearch]: value,
        });

        if (timer) {
            clearTimeout(timer);
            setTimer(null);
        }
        const timeout = setTimeout(() => {
            inputProps.dataFetcher(params).then((res: any) => {
                for (const data of res.data.data) {
                    let label = "";

                    if (typeof inputProps.dataLabel === "string") {
                        label = data[inputProps.dataLabel];
                    } else {
                        label = inputProps.dataLabel(data);
                    }

                    values.push({ value: data[inputProps.dataValue], label: label });
                }

                setOptions(values);
                setRecords(res.data.data);
                setOptionsDisplay(true);
            });
        }, 500);
        setTimer(timeout);
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const value: string = e.currentTarget.value;
        if (value.length >= minSearch) {
            if (value.length === 0) {
                handleEvent(e);
            } else {
                setOptionsDisplay(true);
            }
        }
    };

    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        handleEvent(e);
    };

    const onSelectOption = (e: React.MouseEvent<HTMLElement>) => {
        const value: string = e.currentTarget.dataset.value ?? "";
        const label: string = e.currentTarget.dataset.label ?? "";
        const record = records.find((item: any) => item[inputProps.dataValue] === value);
        const values = [...inputProps.values];
        if (
            values.findIndex((v: OptionsInterface) => {
                return v?.value === value;
            }) === -1
        ) {
            values.push({ label, value });
        }

        inputProps.onSelectOptionCallback && inputProps.onSelectOptionCallback(value, record, values);
        input.onChange(value);
    };

    /**
     * Ferme la liste des résultats lorsqu'on clique hors de cette liste
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                !(listRef.current as any)?.contains(event.target) &&
                !(inputRef.current as any)?.contains(event.target)
            ) {
                setOptionsDisplay(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside, true);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [listRef, input]);

    return (
        <Field
            name={autocompleteFieldName}
            render={({ input }) => (
                <React.Fragment>
                    <div className="field">
                        <div
                            className="field-control-wrapper"
                            role="combobox"
                            aria-controls=""
                            aria-expanded="false"
                            aria-haspopup="listbox"
                            aria-labelledby="downshift-2-label"
                        >
                            <div className="field-control field-control__select-wrapper">
                                <div className="field-control__select is-open">
                                    <input
                                        name={inputProps.name}
                                        id={"autocomplete_" + inputProps.name}
                                        type="text"
                                        className="field-control-select__input"
                                        value={String(input.value)}
                                        onChange={input.onChange}
                                        onKeyUp={onKeyUp}
                                        onFocus={onFocus}
                                        onKeyPress={(e) => {
                                            e.key === "Enter" && e.preventDefault();
                                        }}
                                        placeholder={inputProps.placeholder}
                                        disabled={inputProps.disabled}
                                        autoComplete={inputProps.autoComplete}
                                        ref={inputRef}
                                    />
                                </div>

                                <ul
                                    role="listbox"
                                    aria-labelledby="downshift-2-label"
                                    id="downshift-2-menu"
                                    className={
                                        "field-control-select__list" + (optionsDisplay === false ? " hidden" : "")
                                    }
                                    ref={listRef}
                                >
                                    {options.reduce((memo: any, option: OptionsInterface, index: number) => {
                                        if (
                                            (inputProps?.values || []).findIndex(
                                                (v: OptionsInterface) => "" + v.value === "" + option.value
                                            ) > -1
                                        ) {
                                            return memo;
                                        }
                                        memo.push(
                                            <li
                                                id={`downshift-2-item-${index}`}
                                                role="option"
                                                aria-selected="true"
                                                className="field-control-select__item highlighted"
                                                key={`${inputProps.name}__option__${option.value}`}
                                                data-value={option.value}
                                                data-label={option.label}
                                                onClick={onSelectOption}
                                            >
                                                {option.label}
                                            </li>
                                        );
                                        return memo;
                                    }, [])}
                                </ul>
                            </div>
                        </div>
                    </div>
                    {fieldError && fieldErrorMessage !== undefined && (
                        <Feedback className="field__feedback error">{fieldErrorMessage}</Feedback>
                    )}
                </React.Fragment>
            )}
        />
    );
};
export default AutocompleteSelect;
